import dayjs from "dayjs";
import { FullContainerOverview } from "@/types";

export type ContainersById = ReadonlyMap<number, FullContainerOverview>;

/**
 * The container a one-pickup group is scheduled against. Suggestion, slots and drivers depend only on the
 * shared site, and the deadline is the report date plus a fixed response time, so the earliest report
 * carries the group's earliest deadline. `containers` must not be empty.
 */
export const earliestReported = (containers: FullContainerOverview[]): FullContainerOverview =>
  containers.reduce((earliest, next) => (dayjs(next.reported).isBefore(earliest.reported) ? next : earliest));

/**
 * What makes two queued containers "the same site". The queue carries no site id, so this is the site
 * name; the backend still rejects a batch that spans companies. Swap the key here if the DTO gains an id.
 */
export const siteKeyOf = (container: FullContainerOverview): string => container.siteName;

/**
 * The site the selection is locked to: the first pick's. Ids that have left the queue (scheduled
 * elsewhere, refetched away) are skipped, so a stale first pick can't hold the lock. Null when nothing
 * is picked.
 */
export const lockedSiteOf = (picked: number[], byId: ContainersById): string | null => {
  for (const id of picked) {
    const container = byId.get(id);
    if (container) return siteKeyOf(container);
  }
  return null;
};

/** A container at another site than the locked one can't be picked. Nothing is blocked while unlocked. */
export const isBlocked = (container: FullContainerOverview, lockedSite: string | null): boolean =>
  lockedSite !== null && siteKeyOf(container) !== lockedSite;

/**
 * Ticks or unticks `target`. Unticking is always allowed; ticking a container from another site than
 * the locked one is ignored, so the rule holds for any caller rather than only for the disabled checkbox.
 */
export const togglePicked = (picked: number[], target: FullContainerOverview, byId: ContainersById): number[] => {
  if (picked.includes(target.containerId)) return picked.filter((id) => id !== target.containerId);
  if (isBlocked(target, lockedSiteOf(picked, byId))) return picked;
  return [...picked, target.containerId];
};

const lockedSiteRows = (pageRows: FullContainerOverview[], lockedSite: string) =>
  pageRows.filter((container) => siteKeyOf(container) === lockedSite);

/** Whether the header checkbox reads as checked: the page has locked-site rows and every one is picked. */
export const isPageFullyPicked = (
  picked: number[],
  pageRows: FullContainerOverview[],
  lockedSite: string | null
): boolean => {
  if (lockedSite === null) return false;
  const rows = lockedSiteRows(pageRows, lockedSite);
  return rows.length > 0 && rows.every((container) => picked.includes(container.containerId));
};

/**
 * The header checkbox: ticks or unticks every locked-site row on the current page. Rows from other pages
 * keep their state, and it does nothing before a site is locked — "all" across sites would break the rule.
 */
export const setPageSelection = (
  picked: number[],
  pageRows: FullContainerOverview[],
  lockedSite: string | null,
  on: boolean
): number[] => {
  if (lockedSite === null) return picked;
  const pageIds = lockedSiteRows(pageRows, lockedSite).map((container) => container.containerId);
  return on
    ? [...picked, ...pageIds.filter((id) => !picked.includes(id))]
    : picked.filter((id) => !pageIds.includes(id));
};
